"use client";
import React from "react";
import { motion } from "framer-motion";
import { Users, Briefcase, GraduationCap, ArrowRight } from "lucide-react";
import { useRouter } from "next/navigation";

const segments = [
  {
    title: "Founders",
    icon: <Briefcase className="w-8 h-8 text-[#063258]" />,
    description: "Clarity on strategy, investor access, and a community of high-performing peers.",
    outcomes: ["Strategic Clarity", "Investor Networking", "GTM Playbooks"],
    color: "bg-blue-50",
    borderColor: "border-blue-100",
    cta: "Join Founder's Trail"
  },
  {
    title: "Creators",
    icon: <Users className="w-8 h-8 text-[#FFB619]" />,
    description: "Monetization strategies, personal branding, and collaborative growth ecosystems.",
    outcomes: ["Brand Growth", "Monetization Path", "Peer Collaboration"],
    color: "bg-yellow-50",
    borderColor: "border-yellow-100",
    cta: "Explore Creator's Trail"
  },
  {
    title: "Students",
    icon: <GraduationCap className="w-8 h-8 text-green-600" />,
    description: "Career clarity, real-world exposure, and foundational networks for the future.",
    outcomes: ["Career Direction", "Industry Exposure", "Early Networking"],
    color: "bg-green-50",
    borderColor: "border-green-100",
    cta: "Campus Trail"
  }
];

const SegmentsSection = () => {
  const router = useRouter();

  return (
    <section className="w-full py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="font-bricolage text-4xl md:text-5xl font-bold text-[#1d1d1d] mb-4"
          >
            Tailored Experiences for Every Journey
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="font-jakarta text-lg text-gray-600 max-w-2xl mx-auto"
          >
            Whether you&apos;re building a startup, growing a brand, or finding your path, we have a curated trail for you.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {segments.map((segment, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`p-8 rounded-[32px] border ${segment.borderColor} ${segment.color} flex flex-col h-full hover:shadow-xl transition-all duration-300 group`}
            >
              <div className="mb-6 p-4 rounded-2xl bg-white w-fit shadow-sm group-hover:scale-110 transition-transform duration-300">
                {segment.icon}
              </div>
              <h3 className="font-bricolage text-2xl font-bold text-[#1d1d1d] mb-4">{segment.title}</h3>
              <p className="font-jakarta text-gray-600 mb-6 flex-grow leading-relaxed">
                {segment.description}
              </p>
              <ul className="mb-8 space-y-3">
                {segment.outcomes.map((outcome, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#063258]" />
                    {outcome}
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => router.push("/apply")}
                className="w-full py-4 px-6 bg-[#1d1d1d] text-white rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-[#063258] transition-colors duration-300"
              >
                {segment.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SegmentsSection;
